<script setup lang="ts">
  import { computed, nextTick, ref } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import V2SetupPickerList from '@/components/v2/store-setup/shared/V2SetupPickerList.vue';
  import type { IPickerOption } from '@/domain/store-setup/location-step';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Categoría» of a dish (plan §4.5.1): a select whose list opens in a
   * `PosUiPopover` (fixed, flips above near the bottom of the screen, never
   * clipped by the scrolling list). The trigger is named by the field label
   * plus the chosen category («Categoría, Pizzas»).
   */
  const props = withDefaults(
    defineProps<{
      value?: string | null;
      options: IPickerOption[];
      /** Id of the field label (`PosUiField` `label-id`). */
      labelId: string;
      /** Accessible name of the list. */
      listLabel: string;
      disabled?: boolean;
    }>(),
    { value: null, disabled: false }
  );
  const emit = defineEmits<{ (e: 'input', value: string): void }>();

  const valueId = nextDomId('menu-category-value');
  const root = ref<HTMLElement | null>(null);

  const selected = computed(
    () => props.options.find((option) => option.value === props.value) ?? null
  );

  async function pick(value: string, close: () => void) {
    close();
    if (value !== props.value) emit('input', value);
    // The focused search box went away with the panel: the focus goes back to the select.
    await nextTick();
    const trigger = root.value?.querySelector('.category-trigger');
    if (trigger instanceof HTMLElement) trigger.focus({ preventScroll: true });
  }
</script>

<template>
  <div ref="root" class="v2-menu-category-select">
    <PosUiPopover
      class="category-popover"
      placement="bottom"
      align="start"
      :disabled="disabled"
    >
      <template #trigger="{ open, toggle }">
        <button
          type="button"
          class="category-trigger"
          aria-haspopup="listbox"
          :aria-expanded="open ? 'true' : 'false'"
          :aria-labelledby="`${labelId} ${valueId}`"
          :disabled="disabled"
          @click="toggle"
        >
          <span :id="valueId" class="trigger-label">{{
            selected ? selected.label : ''
          }}</span>
          <PosUiIcon
            name="chevron-down"
            class="trigger-chevron"
            :class="{ 'is-open': open }"
          />
        </button>
      </template>
      <template #default="{ close }">
        <V2SetupPickerList
          :options="options"
          :value="value"
          :search-label="$t('posV2.search')"
          :list-label="listLabel"
          :empty-text="$t('posV2.noResults')"
          @select="(picked) => pick(picked, close)"
        />
      </template>
    </PosUiPopover>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-category-select {
    @apply tw-flex tw-w-full tw-min-w-0;
    .category-popover {
      @apply tw-flex tw-w-full tw-min-w-0;
    }
    .category-trigger {
      @apply tw-flex tw-h-11 tw-w-full tw-min-w-0 tw-cursor-pointer tw-items-center tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-surface tw-px-3 tw-text-left tw-transition-colors;
      &:focus-visible {
        @apply tw-border-orange-600 tw-outline-none tw-ring-1 tw-ring-orange-600;
      }
      &:disabled {
        @apply tw-cursor-not-allowed tw-bg-neutral-50 tw-opacity-60;
      }
    }
    .trigger-label {
      @apply tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-text-neutral-700;
    }
    .trigger-chevron {
      // Margin, not flex gap: old POS hardware (Chrome < 84).
      @apply tw-ml-1 tw-shrink-0 tw-text-lg tw-text-neutral-400 tw-transition-transform;
      &.is-open {
        @apply tw-rotate-180;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-menu-category-select .trigger-chevron {
      @apply tw-transition-none;
    }
  }
</style>
